import { Injectable } from '@angular/core';
import { ApiHelper } from '../apiHelper';
import { ConstantMan } from '../constantMan';
import { BaseDAO } from './baseDAO';
import { Observable } from 'rxjs';
import { HttpHeaders } from '@angular/common/http';
import { Payout } from 'anchor-shared/lib/model/entity/payout';
import { Charter } from 'anchor-shared/lib/model/entity/charter';
import { VesselRate } from 'anchor-shared/lib/model/entity/vessel-rate';
import { StripeBalanceResponse } from '../../models/interface/stripe/stripe-balance-response';
import { UserWithChartersAndPayouts } from '../../../app/portal/charters/unpaid-charters/unpaid-charters.component';
import { SubPayout } from 'anchor-shared/lib/model/entity/subPayout';

@Injectable()
export class PayoutDAO extends BaseDAO<Payout> {
  constructor(public readonly apiHelper: ApiHelper) {
    super(apiHelper, ConstantMan.API.RESOURCE.PAYOUT);
  }

  public createMultiple(data: Array<any>): Observable<any> {
    let url: string =
      this.apiHelper.getServiceName() + this.getResource() + '/charter-payouts';
    let method: string = 'POST';
    let headers: HttpHeaders = this.apiHelper.getDefaultHeader();
    let httpRequest: Observable<any> = this.apiHelper.apiCall<any>(
      url,
      method,
      null,
      data,
      headers,
      null,
    );

    return Observable.create((observer) => {
      httpRequest.subscribe(
        (res: any) => {
          observer.next(res);
          observer.complete();
        },
        (error) => {
          observer.error(error);
        },
      );
    });
  }

  updateMultiplePayoutsByCharter(
    payouts: (Payout | Partial<Payout>)[],
  ): Observable<Payout[]> {
    let url: string =
      this.apiHelper.getServiceName() +
      this.getResource() +
      '/update-payouts-on-charter/';
    const method = 'PUT';
    const headers: HttpHeaders = this.apiHelper.getDefaultHeader();
    return this.apiHelper.apiCall<Payout[]>(
      url,
      method,
      null,
      payouts,
      headers,
    );
  }

  public updateFromPartial(
    payoutId: number,
    partialPayout: Partial<Payout>,
  ): Observable<any> {
    let url: string =
      this.apiHelper.getServiceName() +
      this.getResource() +
      '/partial/' +
      payoutId;
    const method = 'PUT';
    const headers: HttpHeaders = this.apiHelper.getDefaultHeader();
    return this.apiHelper.apiCall<Payout[]>(
      url,
      method,
      null,
      partialPayout,
      headers,
    );
  }

  public deleteMultiple(ids: number[]): Observable<any> {
    let url: string =
      this.apiHelper.getServiceName() + this.getResource() + '/delete-multiple';
    let method: string = 'DELETE';
    let headers: HttpHeaders = this.apiHelper.getDefaultHeader();
    let data = { ids: ids };
    let httpRequest: Observable<any> = this.apiHelper.apiCall<any>(
      url,
      method,
      null,
      data,
      headers,
      null,
    );

    return Observable.create((observer) => {
      httpRequest.subscribe(
        (res: any) => {
          observer.next(res);
          observer.complete();
        },
        (error) => {
          observer.error(error);
        },
      );
    });
  }

  public updateMultiple(_data: Payout[]): Observable<Payout[]> {
    let url: string =
      this.apiHelper.getServiceName() +
      this.getResource() +
      '/update-charter-payouts';
    let method = 'POST';
    let headers: HttpHeaders = this.apiHelper.getDefaultHeader();
    return this.apiHelper.apiCall<Payout[]>(
      url,
      method,
      null,
      _data,
      headers,
      null,
    );
  }

  public loadForProcessing(): Observable<Payout[]> {
    let url: string =
      this.apiHelper.getServiceName() +
      this.getResource() +
      '/admin/processing';
    const method = 'GET';
    const headers: HttpHeaders = this.apiHelper.getDefaultHeader();
    const data = {};
    return this.apiHelper.apiCall<Payout[]>(url, method, null, data, headers);
  }

  public charterPayoutRequest(
    charterPayoutRequest: CharterPayoutRequest,
  ): Observable<Payout[]> {
    let url: string =
      this.apiHelper.getServiceName() +
      this.getResource() +
      '/admin/charter-payout-request';
    const method = 'PUT';
    const headers: HttpHeaders = this.apiHelper.getDefaultHeader();
    return this.apiHelper.apiCall<Payout[]>(
      url,
      method,
      null,
      charterPayoutRequest,
      headers,
    );
  }

  payWithStripe(stripeUserId: string, netPay: number): Observable<any> {
    const url =
      this.apiHelper.getServiceName() +
      this.getResource() +
      '/pay-with-stripe/' +
      stripeUserId;
    const method = 'POST';
    const headers = this.apiHelper.getDefaultHeader();
    const data = {
      stripeUserId: stripeUserId,
      netPay: netPay,
    };
    return this.apiHelper.apiCall<boolean>(url, method, null, data, headers);
  }

  reverseStripeTransfer(transferId: string, amount: number): Observable<any> {
    const url =
      this.apiHelper.getServiceName() +
      this.getResource() +
      '/reverse-stripe-transfer/' +
      transferId;
    const method = 'POST';
    const headers = this.apiHelper.getDefaultHeader();
    const data = {
      transferId: transferId,
      amount: amount,
    };
    return this.apiHelper.apiCall<boolean>(url, method, null, data, headers);
  }

  bulkPayWithStripe(
    bulkStripeTransferInfo: StripeTransferInfo[],
    payableUsers: UserWithChartersAndPayouts[],
  ): Observable<boolean> {
    const url =
      this.apiHelper.getServiceName() +
      this.getResource() +
      '/bulk-pay-with-stripe/';
    const method = 'PUT';
    const headers = this.apiHelper.getDefaultHeader();
    const data = {
      bulkStripeTransferInfo: bulkStripeTransferInfo,
      payableUsers: payableUsers,
    };
    return this.apiHelper.apiCall<boolean>(url, method, null, data, headers);
  }

  getStripeBalance(): Observable<StripeBalanceResponse> {
    const url =
      this.apiHelper.getServiceName() +
      this.getResource() +
      '/get/stripe-balance';
    const method = 'GET';
    const headers = this.apiHelper.getDefaultHeader();
    return this.apiHelper.apiCall<StripeBalanceResponse>(
      url,
      method,
      null,
      null,
      headers,
    );
  }

  public updateManualPayouts(
    manualPayouts: Payout[],
    captainId?: number,
    ownerId?: number,
  ): Observable<Payout[]> {
    let url: string =
      this.apiHelper.getServiceName() +
      this.getResource() +
      '/admin/update-manual-payouts';
    const method = 'POST';
    const headers: HttpHeaders = this.apiHelper.getDefaultHeader();
    const data = {
      manualPayouts,
      captainId,
      ownerId,
    };
    return this.apiHelper.apiCall<Payout[]>(url, method, null, data, headers);
  }

  public createMockPayoutsForPricingDetails(
    vesselRates: VesselRate[],
    charter: Charter,
  ): Observable<Payout[]> {
    let url: string =
      this.apiHelper.getServiceName() +
      this.getResource() +
      '/admin/create-mock-payouts';
    const method = 'POST';
    const headers: HttpHeaders = this.apiHelper.getDefaultHeader();
    const data = {
      vesselRates,
      charter,
    };
    return this.apiHelper.apiCall<Payout[]>(url, method, null, data, headers);
  }

  public createBOMockPayoutsForPricingDetails(
    vesselRates: VesselRate[],
    charter: Charter,
  ): Observable<Payout[]> {
    let url: string =
      this.apiHelper.getServiceName() +
      this.getResource() +
      '/boat-owner/create-mock-payouts';
    const method = 'POST';
    const headers: HttpHeaders = this.apiHelper.getDefaultHeader();
    const data = {
      vesselRates,
      charter,
    };
    return this.apiHelper.apiCall<Payout[]>(url, method, null, data, headers);
  }

  public getAllPayoutsByUserId(userId: number): Observable<Payout[]> {
    let url: string =
      this.apiHelper.getServiceName() +
      this.getResource() +
      '/get-all-payouts-by-user-id/' +
      userId;
    const method = 'GET';
    const headers: HttpHeaders = this.apiHelper.getDefaultHeader();
    return this.apiHelper.apiCall<Payout[]>(url, method, null, userId, headers);
  }

  public sendRequestStripeSignUpEmail(
    customerName: string,
    payoutAmount: string,
    userId: number,
  ): Observable<boolean> {
    let url: string =
      this.apiHelper.getServiceName() +
      this.getResource() +
      '/request-stripe-signup-email';
    const method = 'POST';
    const headers: HttpHeaders = this.apiHelper.getDefaultHeader();
    const data = {
      customerName,
      payoutAmount,
      userId,
    };
    return this.apiHelper.apiCall<boolean>(url, method, null, data, headers);
  }
}

export interface CharterPayoutRequest {
  charterId: number;
  captainChangeId?: number;
  durationChangeValue?: number;
  crewMemberIds?: number[];
  date: string;
  /**
   * makes the other args useless
   */
  createFromScratch?: boolean;
  partialCharter?: Partial<Charter>;
}

export interface StripeTransferInfo {
  userId: number;
  stripeUserId: string;
  payout: {
    id: number;
    paidDate: Date;
    paidAtTheDock: boolean;
    basePay: string;
  }[];
  newPayouts: Partial<Payout>[];
  subPayout: {
    id: number;
    paidDate: Date;
    paidAtTheDock: boolean;
    basePay: string;
  }[];
  newSubPayouts: Partial<SubPayout>[];
  netPay: number;
}
